'use client';

import Image from 'next/image';
import Link from 'next/link';

import { FeatureProductTileBase } from '@/fo/feature/product/tile/base';
import { ArticlePostContent, ImagePosition } from '@/fo/ui/article/page/post';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitTypographyBase } from '@/fo/ui/kit/typography/base';
import { utilGetLinkRel } from '@/fo/util/get-link-rel';

import { VideoPlayer } from '../VideoPlayer/VideoPlayer';

import { styles } from './ContentItem.styles';

export interface ContentItemProps {
  content: ArticlePostContent;
  isAuthorized: boolean;
  articleTitle: string;
}

export function ContentItem({
  content,
  isAuthorized,
  articleTitle
}: ContentItemProps) {
  const slots = styles();

  const renderImage = () => {
    const image = (
      <Image
        className={slots.image()}
        src={content.image ?? ''}
        alt={content.imageAlt ?? ''}
        fill
      />
    );

    const imageInner = <div className={slots.imageInner()}>{image}</div>;

    if (!content.imageLink) {
      return imageInner;
    }

    return (
      <Link
        href={content.imageLink}
        target="_blank"
        rel={utilGetLinkRel(content.imageLink, true)}
        className="block"
      >
        {imageInner}
      </Link>
    );
  };

  if (
    !content.text &&
    !content.image &&
    !content.video &&
    !content.products?.length
  ) {
    return null;
  }

  return (
    <div className={slots.base()}>
      {content.image && content.imagePosition !== ImagePosition.Bottom && (
        <div
          className={slots.imageContainer({
            position: content.imagePosition ?? ImagePosition.Top
          })}
        >
          {renderImage()}

          {content.imageAlt && content.imagePosition === ImagePosition.Top && (
            <div className={slots.imageAlt()}>{content.imageAlt}</div>
          )}
        </div>
      )}
      <UiKitTypographyBase
        as="div"
        className={slots.text()}
      >
        {content.text}
      </UiKitTypographyBase>
      {content.image && content.imagePosition === ImagePosition.Bottom && (
        <div
          className={slots.imageContainer({
            position: content.imagePosition
          })}
        >
          {renderImage()}

          {content.imageAlt && (
            <div className={slots.imageAlt()}>{content.imageAlt}</div>
          )}
        </div>
      )}
      {content.video && (
        <VideoPlayer
          video={content.video}
          videoImage={content.videoImage ?? undefined}
          title={articleTitle}
        />
      )}
      {!!content.products?.length && (
        <div className={slots.products()}>
          <UiKitCarouselBase
            className={slots.carousel()}
            arrowPrevClass={slots.arrowPrev()}
            arrowNextClass={slots.arrowNext()}
            loop={false}
          >
            {content.products.map((product, index) => (
              <FeatureProductTileBase
                key={product.sku}
                data={product}
                isAuthorized={isAuthorized}
                listName="article"
                positionIndex={index}
              />
            ))}
          </UiKitCarouselBase>
        </div>
      )}
    </div>
  );
}
